import { AccessPermissions, IAccessPermissions } from "../../models/collaboration/ObjectUserPermissions";
import { IVideoLink } from "../../models/digitalMedia/VideoLink";
import { typeUniqueId } from "../../types";
import { IVideoLinkViewModel } from ".";


export class VideoLinkViewModel implements IVideoLinkViewModel {

  /**
   * @method Constructor method
   * @param {IVideoLink} videoLink A reference to the underlying VideoLink object
   * @param {typeUniqueId} parentTopicItemId Id of the TopicItem that is the parent of the digital media object
   * @param {typeUniqueId} ancestorTopicId Id of the Topic that is a direct ancestor of the digital media object
   * @param {typeUniqueId} parentTopicItemId Id of the Channel that is a direct ancestor of the digital media object
   * @param {IAccessPermissions} (optional) accessPermissionToAncestorChannel The current user's access permission to the ancestor channel.
   */
  constructor(
    videoLink: IVideoLink,
    parentTopicItemId: typeUniqueId,
    ancestorTopicId: typeUniqueId,
    ancestorChannelId: typeUniqueId,
    accessPermissionToAncestorChannel?: IAccessPermissions
  ) {

    // assign properties from ctor parameters
    this._videoLink = videoLink;
    this._parentTopicItemId = parentTopicItemId;
    this._ancestorTopicId = ancestorTopicId;
    this._ancestorChannelId = ancestorChannelId;

    if (accessPermissionToAncestorChannel) {
      this._accessPermissionToAncestorChannel = accessPermissionToAncestorChannel;
    }
  }


  /*-----------------------------------------------*/
  /**
   * @property {IVideoLink} _videoLink A reference to the underlying VideoLink object
   */
  private _videoLink: IVideoLink;

  /**
   * @method videoLink Getter method for _videoLink
   */
  get videoLink(): IVideoLink {
    return this._videoLink;
  }

  /**
   * @method videoLink Setter method for _videoLink
   * @param {IVideoLink} value The input value for setting _videoLink
   */
  set videoLink(value: IVideoLink) {
    this._videoLink = value;
  }
  /*-----------------------------------------------*/

  /*-----------------------------------------------*/
  /**
   * @property {typeUniqueId} _parentTopicItemId Id of the TopicItem that is the parent of the digital media object
   */
  private _parentTopicItemId: typeUniqueId;

  /**
   * @method parentTopicItemId Getter method for _parentTopicItemId
   */
  get parentTopicItemId(): typeUniqueId {
    return this._parentTopicItemId;
  }

  /**
   * @method parentTopicItemId Setter method for _parentTopicItemId
   * @param {typeUniqueId} value The input value for setting _parentTopicItemId
   */
  set parentTopicItemId(value: typeUniqueId) {
    this._parentTopicItemId = value;
  }
  /*-----------------------------------------------*/

  /*-----------------------------------------------*/
  /**
   * @property {typeUniqueId} _ancestorTopicId Id of the Topic that is a direct ancestor of the digital media object
   */
  private _ancestorTopicId: typeUniqueId;

  /**
   * @method ancestorTopicId Getter method for _ancestorTopicId
   */
  get ancestorTopicId(): typeUniqueId {
    return this._ancestorTopicId;
  }

  /**
   * @method ancestorTopicId Setter method for _ancestorTopicId
   * @param {typeUniqueId} value The input value for setting _ancestorTopicId
   */
  set ancestorTopicId(value: typeUniqueId) {
    this._ancestorTopicId = value;
  }
  /*-----------------------------------------------*/

  /*-----------------------------------------------*/
  /**
   * @property {typeUniqueId} _ancestorChannelId Id of the Channel that is a direct ancestor of the digital media object
   */
  private _ancestorChannelId: typeUniqueId;

  /**
   * @method ancestorChannelId Getter method for _ancestorChannelId
   */
  get ancestorChannelId(): typeUniqueId {
      return this._ancestorChannelId;
  }

  /**
   * @method ancestorChannelId Setter method for _ancestorChannelId
   * @param {typeUniqueId} value The input value for setting _ancestorChannelId
   */
  set ancestorChannelId(value: typeUniqueId) {
      this._ancestorChannelId = value;
  }
  /*-----------------------------------------------*/

  /*-----------------------------------------------*/
  /**
   * @property {IAccessPermissions} _accessPermissionToAncestorChannel The current user's access permission to the ancestor channel.
   */
  private _accessPermissionToAncestorChannel: IAccessPermissions = new AccessPermissions();

  /**
   * @method accessPermissionToAncestorChannel Getter method for _accessPermissionToAncestorChannel
   */
  get accessPermissionToAncestorChannel(): IAccessPermissions {
      return this._accessPermissionToAncestorChannel;
  }

  /**
   * @method accessPermissionToAncestorChannel Setter method for _accessPermissionToAncestorChannel
   * @param {IUserAccessPermissionsForObject} value The input value for setting _accessPermissionToAncestorChannel
   */
  set accessPermissionToAncestorChannel(value: IAccessPermissions) {
      this._accessPermissionToAncestorChannel = value;
  }
  /*-----------------------------------------------*/

}